import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { CalendarClock, CheckCircle, PauseCircle, XCircle } from 'lucide-react';
import { formatInZone, formatTimeUntil, tzAbbrev } from '../../utils/cronNextRun';
import type { ScheduleSummary } from '../../utils/scheduleRows';

interface PipelineSchedulesHealthStripProps {
  summary: ScheduleSummary;
  now: Date;
}

/**
 * At-a-glance state of the schedules currently in view.
 *
 * Counts come from the *filtered* rows, so the numbers always describe what
 * the table below is showing — the alternative (counting the unfiltered set)
 * produces the familiar bug where "Failing: 3" refers to rows you can't see.
 */
// eslint-disable-next-line max-lines-per-function
export const PipelineSchedulesHealthStrip: React.FC<PipelineSchedulesHealthStripProps> = ({
  summary,
  now
}) => {
  const { active, paused, failing, nextUp } = summary;

  const nextSchedule = nextUp?.schedule;
  const nextInstant = nextUp?.next?.instant;

  return (
    <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
      <Card className='flex items-center gap-3 p-3'>
        <CheckCircle className='h-5 w-5 text-green-500' />
        <div>
          <p className='text-lg font-bold'>{active}</p>
          <p className='text-xs text-muted-foreground'>Active</p>
        </div>
      </Card>

      <Card className='flex items-center gap-3 p-3'>
        <PauseCircle className='h-5 w-5 text-muted-foreground' />
        <div>
          <p className='text-lg font-bold'>{paused}</p>
          <p className='text-xs text-muted-foreground'>Paused</p>
        </div>
      </Card>

      <Card className='flex items-center gap-3 p-3'>
        <XCircle className={`h-5 w-5 ${failing > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
        <div>
          <p className='text-lg font-bold'>{failing}</p>
          <p className='text-xs text-muted-foreground'>Last run failed</p>
        </div>
      </Card>

      <Card className='flex items-center gap-3 p-3'>
        <CalendarClock className='h-5 w-5 text-primary' />
        <div className='min-w-0'>
          {nextInstant && nextSchedule ? (
            <>
              <p className='text-lg font-bold'>{formatTimeUntil(nextInstant, now)}</p>
              <p
                className='truncate text-xs text-muted-foreground'
                title={nextSchedule.schedule_name}
              >
                {nextSchedule.schedule_name} ·{' '}
                {formatInZone(nextInstant, nextSchedule.time_zone, 'h:mm A')}{' '}
                {tzAbbrev(nextInstant, nextSchedule.time_zone)}
              </p>
            </>
          ) : (
            <>
              <p className='text-lg font-bold'>—</p>
              <p className='text-xs text-muted-foreground'>Nothing scheduled</p>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
